import { Injectable } from "@angular/core";
import * as LDClient from "launchdarkly-js-client-sdk";
import { environment } from "../../environments/environment";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";

    @Injectable({
      providedIn: "root",
    })
    export class LaunchDarklyService {
      private client: LDClient.LDClient | null = null;
      private context: LDClient.LDContext | null = null;
      private launchDarklyKey: string = "";
      private flagCache: Map<string, boolean> = new Map();
      private isClientReady: boolean = false;

      constructor() {
        this.initializeClient();
      }

      private async initializeClient() {
        this.setLaunchDarklyKey();

        try {
          const { username } = await getCurrentUser();
          const session = await fetchAuthSession();
          const idTokenPayload = session.tokens?.idToken?.payload;

          this.context = {
            kind: "user",
            key: username,
            email: idTokenPayload?.["email"],
          };
        } catch (error) {
          this.context = {
            kind: "user",
            key: this.generateRandomKey(),
          };
        }

        this.client = LDClient.initialize(this.launchDarklyKey, this.context);

        this.client.on("initialized", () => {
          this.isClientReady = true;
        });
      }

      private setLaunchDarklyKey() {
        switch (environment.domain) {
          case "latouretpetit.app":
            this.launchDarklyKey = "67042e90ca5b0c083609d70d";
            break;
          case "staging.latouretpetit.app":
            this.launchDarklyKey = "67042eaef0598a0829e5f8c1";
            break;
          case "dev.latouretpetit.app":
            this.launchDarklyKey = "67042e90ca5b0c083609d70c";
            break;
          default:
            this.launchDarklyKey = "67043abbef42af0827bc6996";
            break;
        }
      }

      private generateRandomKey(): string {
        return "anon-" + Math.random().toString(36).substr(2, 9);
      }

      private async waitForClientInitialization(): Promise<void> {
        if (this.isClientReady) {
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          this.client?.on("initialized", () => {
            this.isClientReady = true;
            resolve();
          });
        });
      }

      async isFeatureEnabled(flagKey: string): Promise<boolean> {

        if (this.flagCache.has(flagKey)) {
          return this.flagCache.get(flagKey)!;
        }

        await this.waitForClientInitialization();

        const variation = this.client?.variation(flagKey, false) ?? false;
        this.flagCache.set(flagKey, variation);
        return variation;
      }

      async getFeatureFlags(flagKeys: string[]): Promise<Record<string, boolean>> {

        await this.waitForClientInitialization();

        const results: Record<string, boolean> = {};
        for (const flagKey of flagKeys) {
          if (this.flagCache.has(flagKey)) {
            results[flagKey] = this.flagCache.get(flagKey)!;
          } else {
            const variation = this.client?.variation(flagKey, false) ?? false;
            this.flagCache.set(flagKey, variation);
            results[flagKey] = variation;
          }
        }

        return results;
      }

      updateUserContext(kind: string, key: string, email: string) {
        this.context = { kind, key, email };
        this.client = LDClient.initialize(this.launchDarklyKey, this.context);
        this.isClientReady = false;

        this.client.on("initialized", () => {
          this.isClientReady = true;
        });
      }

      closeClient() {
        this.client?.close();
        this.client = null;
        this.isClientReady = false;
      }
    }